import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { accountApi, publicApi } from "@/services/api-client";
import { tokenStore } from "@/services/token-store";
import { useQueryClient } from "@tanstack/react-query";
import type { AccountType } from "@/types/dashboard";

export interface AuthIdentity {
  accountId: string;
  accountStatus: string;
  accountType: AccountType;
  permissions: string[];
  roles: string[];
  userId: string;
}

interface LoginInput {
  email: string;
  password: string;
  two_factor_code?: string;
  account_id?: string;
  account_type?: AccountType;
}

interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  account?: Record<string, unknown>;
  user?: Record<string, unknown>;
}

interface AuthContextValue {
  identity: AuthIdentity | null;
  initializing: boolean;
  login(input: LoginInput): Promise<void>;
  logout(): Promise<void>;
  reloadIdentity(): Promise<void>;
  enterPreviewMode(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEMO_IDENTITY: AuthIdentity = {
  accountId: "acc_demo_bizconnectpay",
  accountStatus: "ACTIVE",
  accountType: "BUSINESS",
  permissions: ["*"],
  roles: ["MERCHANT_ADMIN"],
  userId: "usr_demo_bizconnectpay",
};

function normalizeIdentity(data: Record<string, unknown>): AuthIdentity {
  if (
    !data.user_id ||
    !data.account_id ||
    !["INDIVIDUAL", "BUSINESS"].includes(String(data.account_type))
  ) {
    throw new Error(
      "Le contexte de session fourni par le serveur est incomplet.",
    );
  }
  return {
    accountId: String(data.account_id ?? ""),
    accountStatus: String(data.account_status ?? "ACTIVE"),
    accountType: String(data.account_type ?? "INDIVIDUAL") as AccountType,
    permissions: Array.isArray(data.permissions)
      ? data.permissions.map(String)
      : [],
    roles: Array.isArray(data.roles) ? data.roles.map(String) : [],
    userId: String(data.user_id ?? ""),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [initializing, setInitializing] = useState(true);

  const loadIdentity = useCallback(async () => {
    const response = await publicApi<Record<string, unknown>>("/auth/context");
    setIdentity(normalizeIdentity(response.data));
  }, []);

  useEffect(() => {
    let active = true;
    const tokens = tokenStore.get();
    if (tokens?.accessToken) {
      loadIdentity()
        .catch(() => {
          tokenStore.clear();
          if (active) setIdentity(null);
        })
        .finally(() => {
          if (active) setInitializing(false);
        });
    } else {
      setInitializing(false);
    }
    return () => {
      active = false;
    };
  }, [loadIdentity]);

  useEffect(() => {
    const expire = () => {
      queryClient.clear();
      tokenStore.clear();
      setIdentity(null);
    };
    window.addEventListener("bizconnectpay:session-expired", expire);
    return () =>
      window.removeEventListener("bizconnectpay:session-expired", expire);
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      const response = await accountApi<LoginResponse>("/auth/login", {
        method: "POST",
        body: input,
      });
      tokenStore.set({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
      });

      try {
        queryClient.clear();
        await loadIdentity();
      } catch (error) {
        tokenStore.clear();
        throw error;
      }
    },
    [loadIdentity, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      const token = tokenStore.get()?.accessToken;
      if (token) {
        await accountApi("/auth/logout", {
          method: "POST",
          body: {},
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignorer l'erreur réseau lors de la déconnexion
    } finally {
      queryClient.clear();
      tokenStore.clear();
      setIdentity(null);
    }
  }, [queryClient]);

  const enterPreviewMode = useCallback(() => {
    setIdentity(DEMO_IDENTITY);
  }, []);

  const value = useMemo(
    () => ({
      identity,
      initializing,
      login,
      logout,
      reloadIdentity: loadIdentity,
      enterPreviewMode,
    }),
    [identity, initializing, login, logout, loadIdentity, enterPreviewMode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("AuthProvider is missing.");
  }

  return context;
}
